// ISDP CUSTOM: Entire file — API entity overview card
// Reverse-lookup: finds all components that consume this API via isdp.io/api-endpoints annotation
// and renders their consumed endpoints with method badges
import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { Link } from 'react-router-dom';
import './ApiConsumersEndpointCard.css';
import { methodColor } from './methodColors';

const ANNOTATION_KEY = 'isdp.io/api-endpoints';

interface ParsedEndpoint {
  method: string;
  path: string;
}

interface ConsumerEndpoints {
  componentName: string;
  componentNamespace: string;
  componentKind: string;
  endpoints: ParsedEndpoint[];
}

function parseEndpointsForApi(
  annotation: string,
  apiName: string,
): ParsedEndpoint[] {
  for (const line of annotation.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx === -1) continue;
    const name = trimmed.substring(0, colonIdx).trim();
    if (name !== apiName) continue;
    const endpointsStr = trimmed.substring(colonIdx + 1).trim();
    return endpointsStr
      .split(',')
      .map(ep => ep.trim())
      .filter(Boolean)
      .map(ep => {
        const spaceIdx = ep.indexOf(' ');
        if (spaceIdx === -1) return { method: 'GET', path: ep };
        return {
          method: ep.substring(0, spaceIdx).toUpperCase(),
          path: ep.substring(spaceIdx + 1).trim(),
        };
      });
  }
  return [];
}

export function ApiConsumersEndpointCard() {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const [consumers, setConsumers] = useState<ConsumerEndpoints[]>([]);
  const [loading, setLoading] = useState(true);

  const apiName = entity.metadata.name;

  useEffect(() => {
    // Cancel guard: this card is NOT remounted when navigating between two API
    // entity pages (the /catalog/:kind/:name route element is reused, useEntity just
    // yields a new entity), so apiName changes re-run this effect on the same
    // instance. Without the guard, an earlier request resolving after a later one
    // would render the previous API's consumers on the current API's page.
    let cancelled = false;
    catalogApi
      .getEntities({
        filter: { kind: 'Component' },
      })
      .then(response => {
        if (cancelled) return;
        const result: ConsumerEndpoints[] = [];
        for (const comp of response.items) {
          const annotation = comp.metadata.annotations?.[ANNOTATION_KEY];
          if (!annotation) continue;
          const endpoints = parseEndpointsForApi(annotation, apiName);
          if (endpoints.length > 0) {
            result.push({
              componentName: comp.metadata.name,
              componentNamespace: comp.metadata.namespace || 'default',
              componentKind: comp.kind.toLowerCase(),
              endpoints,
            });
          }
        }
        setConsumers(result);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, apiName]);

  if (loading) {
    return (
      <div className="devhub-api-consumers-card">
        <div className="devhub-api-consumers-card__header">
          Endpoint Consumers
        </div>
        <div className="devhub-api-consumers-card__body">
          <div className="devhub-api-consumers-card__loading">
            <div className="devhub-api-consumers-card__spinner" />
          </div>
        </div>
      </div>
    );
  }

  if (consumers.length === 0) {
    return null;
  }

  return (
    <div className="devhub-api-consumers-card">
      <div className="devhub-api-consumers-card__header">
        Endpoint Consumers
      </div>
      <hr className="devhub-api-consumers-card__divider" />
      <div className="devhub-api-consumers-card__body">
        <p className="devhub-api-consumers-card__description">
          Components that consume specific endpoints of this API (via{' '}
          <code>isdp.io/api-endpoints</code> annotation).
        </p>
        {consumers.map(consumer => {
          const componentPath = `/catalog/${consumer.componentNamespace}/${consumer.componentKind}/${consumer.componentName}`;

          return (
            <div key={consumer.componentName} className="devhub-api-consumers-card__consumer-section">
              <Link to={componentPath} className="devhub-api-consumers-card__consumer-link">
                {consumer.componentName}
              </Link>
              <table className="devhub-api-consumers-table">
                <thead>
                  <tr>
                    <th>Method</th>
                    <th>Endpoint</th>
                  </tr>
                </thead>
                <tbody>
                  {consumer.endpoints.map((ep, i) => (
                    <tr key={i}>
                      <td>
                        <span
                          className="devhub-method-badge"
                          style={{
                            backgroundColor: methodColor(ep.method),
                          }}
                        >
                          {ep.method}
                        </span>
                      </td>
                      <td className="devhub-api-consumers-table__path">
                        {ep.path}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}
